import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List } from 'lucide-react';
import Header from '../components/Header';
import CategoryCard from '../components/CategoryCard';
import InfoSlider from '../components/InfoSlider'; // Importar el nuevo componente
import { categories as initialCategories } from '../data/mockData'; // Importar las categorías iniciales
import { WhatsAppWidget } from 'react-whatsapp-widget';
import 'react-whatsapp-widget/dist/index.css';

const DashboardPage = ({ onCategorySelect }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [categories, setCategories] = useState([]); // Estado local para las categorías

  // Obtener usuario actual desde AuthContext y buscar su carta operativa
  const { user } = require('../context/AuthContext').useAuth();
  // Buscar carta operativa en los usuarios iniciales (simulación)
  const cartaOperativa = (() => {
    if (!user) return null;
    const usuariosDemo = [
      { email: 'admin@empresa.com', avatar: 'https://randomuser.me/api/portraits/men/1.jpg', file: { name: 'CartaOperativa-Admin.pdf', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf' } },
      { email: 'agencia@marketing.com', avatar: 'https://randomuser.me/api/portraits/women/2.jpg', file: { name: 'CartaOperativa-Agencia.pdf', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf' } },
      { email: 'distribuidor@ventas.com', avatar: 'https://randomuser.me/api/portraits/men/3.jpg', file: { name: 'CartaOperativa-Distribuidor.pdf', url: 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf' } }
    ];
    return usuariosDemo.find(u => u.email === user.email)?.file || null;
  })();
  const currentAvatar = user?.avatar;

  // Usar useEffect para inicializar las categorías y reaccionar a cambios si los hubiera
  useEffect(() => {
    // Aquí podrías simular una carga de datos o simplemente usar los datos iniciales
    // Asegúrate de que los datos iniciales tengan la propiedad 'active'
    const categoriesWithActiveStatus = initialCategories.map(cat => ({
      ...cat,
      active: cat.active !== undefined ? cat.active : true // Asegura que 'active' exista, por defecto true
    }));
    setCategories(categoriesWithActiveStatus);
  }, []); // Se ejecuta solo una vez al montar el componente

  // Filtrar solo categorías activas para el dashboard público
  const activeCategories = categories.filter(cat => cat.active);

  const filteredCategories = activeCategories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      {/* Widget de WhatsApp flotante en el dashboard */}
      <div style={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1000 }}>
        <WhatsAppWidget
          phoneNumber="56912345678"
          companyName="Turismo Chirak"
          message="¡Hola! ¿En qué podemos ayudarte?"
          sendButtonText="Enviar"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6" // Flexbox para alinear título y slider
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">
              {t('catalog')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('explore')}
            </p>
          </div>
          <InfoSlider /> {/* El nuevo slider */}
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={(() => {
                  if (filteredCategories.length === 1) {
                    const name = filteredCategories[0].name.toLowerCase();
                    if (name.includes('hotel')) return 'Buscar hoteles...';
                    if (name.includes('servicio')) return 'Buscar servicios...';
                    if (name.includes('nieve')) return 'Buscar nieve...';
                    if (name.includes('all inclusive')) return 'Buscar all inclusive...';
                    if (name.includes('programas')) return 'Buscar programas...';
                  }
                  return 'Buscar categorías...';
                })()}
                className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors">
                <Filter className="w-4 h-4" />
                <span className="text-sm font-medium">Filtros</span>
              </button>
              
              <div className="flex bg-gray-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${
                    viewMode === 'list' 
                      ? 'bg-white shadow-sm text-blue-600' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`grid gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
              : 'grid-cols-1'
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {filteredCategories.map((category, index) => (
            <CategoryCard
              key={category.id}
              category={category}
              onClick={onCategorySelect}
              index={index}
            />
          ))}
        </motion.div>

        {filteredCategories.length === 0 && (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-gray-400 mb-4">
              <Search className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No se encontraron categorías turísticas
            </h3>
            <p className="text-gray-500">
              Prueba otros destinos, servicios o ajusta los filtros de viaje.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;